/**
 * WhisperProgressBar — non-blocking sticky progress indicator shown
 * during Session Config while the Whisper model is downloading.
 *
 * Subscribes to `whisperAdapter` status. Visible only when phase is
 * 'loading' or 'error' — auto-hides on 'ready' (so subsequent sessions
 * with the model already cached never see this bar).
 *
 * Position: sticky to the bottom of the viewport. Background and border
 * intentionally match the bottom action panels elsewhere in the app, so
 * it reads as "system chrome" rather than session content. Tap target
 * is a non-interactive informational strip — there's no "cancel" because
 * the user can still proceed (the WhisperLoadingModal handles the
 * "Continue without transcription" escape at submit time).
 */

import { useEffect, useState } from 'react';
import {
  whisperAdapter,
  type WhisperLoadStatus,
} from '@/services/whisperAdapter';
import { cn } from '@/lib/utils';

export function WhisperProgressBar() {
  const [status, setStatus] = useState<WhisperLoadStatus>(
    whisperAdapter.getStatus(),
  );

  useEffect(() => {
    const unsub = whisperAdapter.subscribe(setStatus);
    return unsub;
  }, []);

  if (status.phase === 'idle' || status.phase === 'ready') {
    return null;
  }

  const isError = status.phase === 'error';
  const pct =
    status.phase === 'loading' ? Math.round(status.progress * 100) : 0;

  return (
    <div
      className={cn(
        'fixed bottom-0 left-0 right-0 z-30 border-t border-border bg-surface-elevated px-4 py-2.5',
        'shadow-[0_-4px_12px_rgba(0,0,0,0.2)]',
      )}
      role="status"
      aria-live="polite"
    >
      <div className="mx-auto flex max-w-2xl items-center gap-3">
        {/* Label */}
        <div className="shrink-0 text-[11px] font-semibold uppercase tracking-wider text-text-secondary">
          {isError ? 'Transcription unavailable' : 'Preparing transcription'}
        </div>

        {/* Progress bar */}
        {!isError && (
          <div
            className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-surface-app"
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className="absolute inset-y-0 left-0 bg-brand-accent transition-[width] duration-300 ease-linear"
              style={{ width: `${pct}%` }}
            />
          </div>
        )}

        {/* Percentage / error mark */}
        <div
          className={cn(
            'shrink-0 font-mono text-xs tabular-nums',
            isError ? 'text-state-error' : 'text-text-muted',
          )}
        >
          {isError ? '⚠' : `${pct}%`}
        </div>
      </div>
    </div>
  );
}
